/**
 * Die Rechenteile der **Versionshistorie** (ADR-0029, Spec v1.3).
 *
 * Sie stehen hier und nicht in der Ansicht, weil zwei Flächen sie brauchen: die
 * Seite und die Kopfleiste, die zum Rahmen gehört (ADR-0025) und deshalb keine
 * Fachlichkeit tragen soll.
 */

/**
 * Ein Block des Eintragstextes: ein Absatz oder eine Liste.
 *
 * **Kein Markdown-Übersetzer.** Der Rumpf eines Fassungstextes ist Prosa in
 * Absätzen und, wo es um Kleinigkeiten geht, eine Strichliste — mehr lässt
 * ADR-0029 an Gestalt nicht zu, und mehr wird hier deshalb nicht gelesen. Eine
 * Bibliothek dafür wäre eine Abhängigkeit für zwei Fälle; ein Zeichen für
 * Auszeichnung im Text (`**fett**`) ist in diesen Texten keine Auszeichnung,
 * sondern ein Fehler des Schreibenden und steht dann sichtbar da.
 */
export type Block = { kind: "paragraph"; text: string } | { kind: "list"; items: string[] };

/**
 * Der Text eines Eintrags als Blöcke.
 *
 * Die Zeilenumbrüche innerhalb eines Absatzes gehören der Datei und nicht dem
 * Text: Sie stehen dort, weil eine Datei bei achtundneunzig Zeichen umbricht.
 * Also werden sie zu Leerzeichen — die Ansicht bricht selbst um.
 */
export function blocksOf(markdown: string): Block[] {
  const blocks: Block[] = [];

  for (const group of markdown.split(/\n[ \t]*\n/)) {
    const lines = group
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line !== "");

    if (lines.length === 0) {
      continue;
    }

    if (lines[0]?.startsWith("- ") === true) {
      blocks.push({ kind: "list", items: itemsOf(lines) });

      continue;
    }

    blocks.push({ kind: "paragraph", text: lines.join(" ") });
  }

  return blocks;
}

/**
 * Was in der Kopfleiste steht — und `null` heißt: **nichts**.
 *
 * Drei Fälle, und der dritte ist der, der sonst gerät: `null` ist „die Fassung
 * ist nicht bekannt" (die Statusroute hat noch nicht geantwortet oder antwortet
 * gar nicht). Dann steht dort kein Wort, statt einen Entwicklungsstand zu
 * behaupten, der eine ausgelieferte Fassung sein könnte. Die leere Zeichenkette
 * dagegen ist eine Auskunft: kein Stempel im Abbild, also ein lokaler Stand
 * (Story 288).
 */
export function runningLabel(version: string | null): string | null {
  if (version === null) {
    return null;
  }

  return version === "" ? "Entwicklungsstand" : version;
}

/**
 * Die Adresse eines Ticketverweises — oder `null`, wenn keine Instanz des
 * Ticketsystems eingestellt ist.
 *
 * Der Server setzt die Adresse bis zur Ticketliste zusammen; hier kommt die
 * Nummer daran. Ohne Adresse gibt es keinen Verweis und keine geratene: Die
 * Nummer bleibt sichtbar, der Beleg damit auch.
 */
export function issueAddress(base: string | null, number: number): string | null {
  return base === null ? null : `${base}/${String(number)}`;
}

/** Die Listeneinträge einer Strichliste; eine umbrochene Zeile gehört ihrem Eintrag. */
function itemsOf(lines: string[]): string[] {
  const items: string[] = [];

  for (const line of lines) {
    const last = items.length - 1;

    if (line.startsWith("- ")) {
      items.push(line.slice(2).trim());
    } else if (last >= 0) {
      items[last] = `${items[last] ?? ""} ${line}`;
    }
  }

  return items;
}
